import { Ref, UIEventHandler, useMemo, useState } from 'react';
import { useNodes as useFlowNodes } from 'reactflow';

import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { Button } from '@components/button';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconAIMagic } from '@components/icons/ai-magic';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconRedo } from '@components/icons/redo';
import { IconRestart } from '@components/icons/restart';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { SizedRelativeSpinner } from '@components/loading-spinner/loading-spinner';
import { Stack } from '@components/stack';
import { TabButton } from '@components/tab-button';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { JourneyReview, ReviewFinding, ReviewSeverity } from 'api/journey-reviews/requests';
import { findingSignature, signatureKey } from 'api/journey-reviews/signature';

import { FindingCard } from './finding-card';
import { LoadingChecklist } from './loading-checklist';
import { countFindingsBySeverity, groupFindingsBySeverity, severityColor } from './review-utils';

import { JourneyNode } from '../node-types/types';

type ResultsTab = 'open' | 'ignored';

export type ReviewPanelState = 'empty' | 'fetching' | 'loading' | 'results' | 'error';

export type ReviewPanelViewProps = {
  state: ReviewPanelState;
  review: JourneyReview | null;
  // The review has landed but the loading checklist is still playing out;
  isFinishing?: boolean;
  onLoadingFinished?: () => void;
  // Only offered while a run is actually in flight, never while it settles.
  onStopReview?: () => void;
  isStoppingReview?: boolean;
  onRunReview: () => void;
  onClose: () => void;
  onJumpTo: (finding: ReviewFinding) => void;
  // Lists, so ignoring a whole severity group is one request.
  onIgnore: (findings: ReviewFinding[]) => void;
  onRestore: (findings: ReviewFinding[]) => void;
  bodyRef?: Ref<HTMLDivElement>;
  onBodyScroll?: UIEventHandler<HTMLDivElement>;
};

const SEVERITY_LABEL_KEY: Record<ReviewSeverity, keyof (typeof locale)['en']> = {
  must_fix: 'mustFix',
  review_recommended: 'reviewRecommended',
  improvement: 'improvements',
};

// Only "improvement" pluralizes; the other two read the same either way.
const severityChipKey = (severity: ReviewSeverity, count: number) =>
  severity === 'improvement' && count === 1 ? 'improvement' : SEVERITY_LABEL_KEY[severity];

export const ReviewPanelView = ({
  state,
  review,
  isFinishing,
  onLoadingFinished,
  onStopReview,
  isStoppingReview,
  onRunReview,
  onClose,
  onJumpTo,
  onIgnore,
  onRestore,
  bodyRef,
  onBodyScroll,
}: ReviewPanelViewProps) => {
  const { t } = useScopedTranslate(locale);

  const subtitle =
    state === 'loading'
      ? t('readingFlow')
      : state === 'results' && review
      ? t('reviewedAgo', {
          time: abbreviateDate(review.dateCreated, 'en', 'datetime-fancy'),
        })
      : t('panelSubtitle');

  return (
    <Container>
      <Header>
        <Stack space={2} orientation="horizontal" align="center">
          <IconAIMagic color="aiGradientTeal100" />
          <Stack space={0}>
            <Text font="bold16" text={t('panelTitle')} />
            <Text font="regular12" color="textHelper" text={subtitle} />
          </Stack>
        </Stack>
        <Stack space={1} orientation="horizontal" align="center">
          {state === 'results' && (
            <IconButton
              icon={<IconRestart />}
              onClick={onRunReview}
              tooltip={t('reviewAgain')}
              aria-label={t('reviewAgain')}
            />
          )}
          <IconButton
            icon={<IconCloseRegular />}
            onClick={onClose}
            tooltip={t('close')}
            aria-label={t('close')}
          />
        </Stack>
      </Header>

      <Body ref={bodyRef} onScroll={onBodyScroll}>
        {state === 'fetching' && (
          <CenteredFill>
            <SizedRelativeSpinner size={32} $skipMargin />
          </CenteredFill>
        )}
        {state === 'empty' && <EmptyState onRunReview={onRunReview} />}
        {state === 'loading' && (
          <Stack space={5} align="center">
            <LoadingChecklist isFinishing={isFinishing} onFinished={onLoadingFinished} />
            {Boolean(onStopReview) && !isFinishing && (
              <Button
                variant="secondary"
                size="sm"
                text={t('stopReview')}
                onClick={onStopReview}
                isDisabled={isStoppingReview}
              />
            )}
          </Stack>
        )}
        {state === 'error' && <ErrorState error={review?.error} onRunReview={onRunReview} />}
        {state === 'results' && review && (
          <Results
            review={review}
            onRunReview={onRunReview}
            onJumpTo={onJumpTo}
            onIgnore={onIgnore}
            onRestore={onRestore}
          />
        )}
      </Body>
    </Container>
  );
};

const EmptyState = ({ onRunReview }: { onRunReview: () => void }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4} align="center">
      <Text font="regular14" color="textSecondary" center text={t('emptyBody')} />
      <Button
        variant="ai-gradient"
        text={t('runReview')}
        iconLeft={<IconAIMagic color="aiGradientTeal100" />}
        onClick={onRunReview}
      />
    </Stack>
  );
};

const ErrorState = ({ error, onRunReview }: { error?: string | null; onRunReview: () => void }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4} align="center">
      {/* The backend writes these for the user — "took too long", "no part of the
          content was checked" — and they say more than the generic line. */}
      <Text font="regular14" color="textNegative" center text={error || t('errorBody')} />
      <Button
        variant="secondary"
        text={t('tryAgain')}
        iconLeft={<IconRedo />}
        onClick={onRunReview}
      />
    </Stack>
  );
};

const Results = ({
  review,
  onRunReview,
  onJumpTo,
  onIgnore,
  onRestore,
}: {
  review: JourneyReview;
  onRunReview: () => void;
  onJumpTo: (finding: ReviewFinding) => void;
  onIgnore: (findings: ReviewFinding[]) => void;
  onRestore: (findings: ReviewFinding[]) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const [tab, setTab] = useState<ResultsTab>('open');
  const active = review.findings.filter((finding) => !finding.ignored);
  const ignored = review.findings.filter((finding) => finding.ignored);
  const counts = countFindingsBySeverity(active);
  const groups = groupFindingsBySeverity(active);

  return (
    <Stack space={4}>
      {active.length === 0 ? (
        <CleanResult
          isPartial={review.partial}
          hasIgnored={ignored.length > 0}
          onRunReview={onRunReview}
        />
      ) : (
        <Stack space={3} orientation="horizontal" align="center" wrap>
          {(Object.keys(counts) as ReviewSeverity[])
            .filter((severity) => counts[severity] > 0)
            .map((severity) => (
              <Stack key={severity} space={1} orientation="horizontal" align="center">
                <Dot $color={severityColor[severity]} />
                <Text
                  font="semibold12"
                  text={`${counts[severity]} ${t(severityChipKey(severity, counts[severity]))}`}
                />
              </Stack>
            ))}
        </Stack>
      )}

      {review.status === 'failed' && (
        <FailedBanner role="alert">
          <Text font="regular12" text={review.error || t('errorBody')} />
        </FailedBanner>
      )}

      {review.partial && Boolean(review.partialNote) && (
        <PartialBanner role="status">
          <Text font="regular12" text={review.partialNote ?? ''} />
        </PartialBanner>
      )}

      <TabButton<ResultsTab>
        fullWidth
        value={tab}
        onChange={setTab}
        options={[
          { value: 'open', label: `${t('openTab')} · ${active.length}` },
          { value: 'ignored', label: `${t('ignoredTab')} · ${ignored.length}` },
        ]}
      />

      {tab === 'ignored' ? (
        ignored.length === 0 ? (
          <Text font="regular14" color="textSecondary" text={t('nothingIgnored')} />
        ) : (
          <AnimatedCardList findings={ignored} onJumpTo={onJumpTo} onRestore={onRestore} />
        )
      ) : (
        groups.map((group) => (
          <HideContainer
            key={group.severity}
            // The glyph is a third of the box, so 24 draws an 8px dot.
            icon={<IconStatusIndicator color={severityColor[group.severity]} size={24} />}
            title={t(SEVERITY_LABEL_KEY[group.severity])}
            counter={group.findings.length}
            caps
            defaultClosed={group.severity === 'improvement'}
            action={
              <Button
                variant="ghost"
                size="xs"
                text={t('ignoreAll')}
                onClick={() => onIgnore(group.findings)}
              />
            }
          >
            <AnimatedCardList findings={group.findings} onJumpTo={onJumpTo} onIgnore={onIgnore} />
          </HideContainer>
        ))
      )}
    </Stack>
  );
};

const CleanResult = ({
  isPartial,
  hasIgnored,
  onRunReview,
}: {
  isPartial: boolean;
  hasIgnored: boolean;
  onRunReview: () => void;
}) => {
  const { t } = useScopedTranslate(locale);

  // Only claim the journey is ready to publish when the whole thing was actually
  // reviewed, and don't call ignored findings "nothing found".
  const body = isPartial ? 'cleanBodyPartial' : hasIgnored ? 'cleanBodyIgnored' : 'cleanBody';

  return (
    <Stack space={5} align="center">
      <SuccessDisc
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 22 }}
      >
        <IconCheckmark size={32} color="elementGreen100" />
      </SuccessDisc>
      <Stack space={2} align="center">
        <Text font="heading2" center text={hasIgnored ? t('cleanTitleIgnored') : t('cleanTitle')} />
        <Text font="regular14" color="textHelper" center text={t(body)} />
      </Stack>
      <Button
        variant="ai-gradient"
        size="sm"
        text={t('reviewAgain')}
        iconRight={<IconRestart />}
        onClick={onRunReview}
      />
    </Stack>
  );
};

const AnimatedCardList = ({
  findings,
  onJumpTo,
  onIgnore,
  onRestore,
}: {
  findings: ReviewFinding[];
  onJumpTo: (finding: ReviewFinding) => void;
  onIgnore?: (findings: ReviewFinding[]) => void;
  onRestore?: (findings: ReviewFinding[]) => void;
}) => {
  // Only for the card icons. Subscribing here rather than in Results keeps the
  // counts, chips and groups off reactflow's per-drag re-render path.
  const nodes = useFlowNodes() as JourneyNode[];
  const nodesById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);

  return (
    <div>
      {/* When a card is ignored/restored it leaves this list; AnimatePresence
          collapses it out (height + opacity) before it unmounts. */}
      <AnimatePresence initial={false}>
        {findings.map((finding) => (
          <CardSlot
            key={signatureKey(findingSignature(finding))}
            initial={false}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.2 }}
          >
            <FindingCard
              finding={finding}
              node={finding.target.id ? nodesById.get(finding.target.id) : undefined}
              onJumpTo={onJumpTo}
              onIgnore={onIgnore && ((one) => onIgnore([one]))}
              onRestore={onRestore && ((one) => onRestore([one]))}
            />
          </CardSlot>
        ))}
      </AnimatePresence>
    </div>
  );
};

const SuccessDisc = styled(motion.div)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: pxToRem(56),
  height: pxToRem(56),
  marginTop: pxToRem(24),
  borderRadius: '50%',
  background: theme.color.greenTrans10,
  boxShadow: `0 0 0 ${pxToRem(8)} ${theme.color.greenTrans5}`,
});

const CardSlot = styled(motion.div)({
  overflow: 'hidden',
  marginBottom: pxToRem(8),
});

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  height: '100%',
});

const Header = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: pxToRem(16),
  borderBottom: `1px solid ${theme.color.border10}`,
  flexShrink: 0,
});

const Body = styled.div({
  padding: pxToRem(16),
  paddingBottom: pxToRem(40),
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
});

const CenteredFill = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  height: '100%',
  paddingTop: pxToRem(96),
});

const FailedBanner = styled.div({
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
  borderRadius: pxToRem(4),
  background: theme.color.redTrans10,
});

const PartialBanner = styled.div({
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
  borderRadius: pxToRem(4),
  background: theme.color.orangeTrans10,
});

const Dot = styled.span<{ $color: Color }>((props) => ({
  width: pxToRem(8),
  height: pxToRem(8),
  borderRadius: '50%',
  background: theme.color[props.$color],
  flexShrink: 0,
}));

export const locale = {
  en: {
    panelTitle: 'Review my journey',
    panelSubtitle: 'Catch issues before you publish',
    readingFlow: 'Reading the whole flow…',
    stopReview: 'Stop review',
    reviewedAgo: 'Last reviewed: {time}',
    emptyBody:
      'Run an AI review to catch broken triggers/actions, dead-end paths, unused fields, and weak copy before you publish.',
    runReview: 'Review my journey',
    reviewAgain: 'Review again',
    close: 'Close',
    tryAgain: 'Try again',
    errorBody: "The review couldn't be completed. Please try again.",
    cleanTitle: 'No issues found',
    cleanBody: 'Triggers, branches, traveller-facing texts, and fields all look ready to publish.',
    cleanTitleIgnored: 'No open issues',
    cleanBodyIgnored: 'Everything else looks ready to publish — your ignored findings are below.',
    cleanBodyPartial: 'Nothing to flag in the part of the journey I reviewed.',
    mustFix: 'Must fix',
    reviewRecommended: 'Review recommended',
    improvements: 'Improvements',
    improvement: 'Improvement',
    ignoreAll: 'Ignore all',
    openTab: 'Open',
    ignoredTab: 'Ignored',
    nothingIgnored:
      "Nothing ignored yet. Findings you ignore move here, and a later review won't raise them again.",
  },
};
